import Hls from 'hls.js';

/**
 * Custom HLS fragment loader yang auto-decrypt segmen `.ts` ber-header
 * magic "shortmax" (ShortMax / StoryReel / VibeShort / DramaVerse).
 * Port dari player reference resmi QuickPlay.
 *
 * Skema: tiap segmen ter-enkripsi diawali 1024-byte header. Byte 0..8 =
 * "shortmax". Byte 16..20 = keyOffset (ASCII int), 20..24 = encLen. Key
 * 16-byte ASCII pada headerStr[keyOffset]. Cipher = slice[1024,1024+encLen]
 * AES-128-CBC (IV konstan "shortmax00000000"). Hasil decrypt + sisa byte
 * setelah cipher = TS bersih.
 *
 * Loader ini AMAN dipakai universal untuk semua HLS: segmen tanpa magic
 * (freereels/netshort HLS biasa) diteruskan apa adanya (pass-through).
 *
 * `Hls.DefaultConfig.loader` di-extend supaya perilaku loader bawaan
 * (retry, timeout, playlist) tetap ada; kita hanya override `load`.
 */

const MAGIC = 'shortmax';
const IV = new TextEncoder().encode('shortmax00000000');

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyLoader = any;

async function decryptSegment(rawBytes: Uint8Array): Promise<Uint8Array> {
  const dec = new TextDecoder();
  const offsetStr = dec.decode(rawBytes.slice(16, 20));
  const encLenStr = dec.decode(rawBytes.slice(20, 24));
  const keyOffset = Number.parseInt(offsetStr, 10);
  const encLen = Number.parseInt(encLenStr, 10);

  const headerStr = dec.decode(rawBytes.slice(0, 1024));
  const keyStr = headerStr.slice(keyOffset, keyOffset + 16);
  const keyBytes = new TextEncoder().encode(keyStr);

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyBytes,
    { name: 'AES-CBC' },
    false,
    ['decrypt'],
  );
  const encSlice = rawBytes.slice(1024, 1024 + encLen);
  const decryptedPadded = await crypto.subtle.decrypt(
    { name: 'AES-CBC', iv: IV },
    cryptoKey,
    encSlice,
  );
  const decHeader = new Uint8Array(decryptedPadded);
  const restTs = rawBytes.slice(1024 + encLen);
  const cleanTs = new Uint8Array(decHeader.length + restTs.length);
  cleanTs.set(decHeader, 0);
  cleanTs.set(restTs, decHeader.length);
  return cleanTs;
}

/**
 * Buat kelas fLoader yang extend loader bawaan Hls.js. Dipakai via
 * `new Hls({ fLoader: makeShortmaxLoader() })`.
 */
export function makeShortmaxLoader(): AnyLoader {
  const Base = Hls.DefaultConfig.loader as AnyLoader;

  return class ShortmaxLoader extends Base {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    load(context: any, config: any, callbacks: any): void {
      const isSegment =
        context.responseType === 'arraybuffer' &&
        !String(context.url).includes('.m3u8');
      if (!isSegment) {
        super.load(context, config, callbacks);
        return;
      }
      // Bungkus onSuccess untuk mendekripsi payload segmen bila ber-magic.
      const origOnSuccess = callbacks.onSuccess;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      callbacks.onSuccess = (response: any, stats: any, ctx: any, networkDetails: any) => {
        const data = response.data as ArrayBuffer;
        const bytes = new Uint8Array(data);
        if (bytes.length > 1024) {
          const magic = new TextDecoder().decode(bytes.slice(0, 8));
          if (magic === MAGIC) {
            decryptSegment(bytes)
              .then((clean) => {
                response.data = clean.buffer;
                origOnSuccess(response, stats, ctx, networkDetails);
              })
              .catch(() => {
                // Gagal decrypt → teruskan raw (biar HLS.js yg tangani error).
                origOnSuccess(response, stats, ctx, networkDetails);
              });
            return;
          }
        }
        origOnSuccess(response, stats, ctx, networkDetails);
      };
      super.load(context, config, callbacks);
    }
  };
}
